import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, timeout } from "rxjs";
import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { Audit } from "../../model/audit/audit.model";
import { IQueryFilter, QueryResult } from "../../model/query.filter.class";
import { JwtService } from "../../services/auth/jwt.service";
import { SocketData } from "../../services/web-socket/contract/web-socket.contract";
import { removeUndefined } from "../../util/object.util";
import { createUrl, createWSUrl, queryToParams } from "../api.util";
import { GenericApi, HasIdOrExternalValueOrExternalIdOrServiceName } from "../generic.api";

// Convenience Type
type T = Audit;
type I = Audit;

@Injectable({
  providedIn: "root",
})
export class AuditApi extends GenericApi<T> {
  public path = "audit";

  constructor(
    public httpClient: HttpClient,
    private readonly jwtService: JwtService,
  ) {
    super(httpClient);
  }

  createTimeout = (): Observable<{ success: boolean }> => {
    return this.httpClient.post<{ success: boolean }>(createUrl(this.path, `timeout-audit`), {});
  };

  get = (id: number): Observable<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>> => {
    return this.httpClient.get<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>(
      createUrl(this.path, id),
    );
  };

  list = (
    query: IQueryFilter,
  ): Observable<QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>> => {
    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path), {
      params: queryToParams(query),
    });
  };

  getAllLogs = (
    skip: number,
    limit: number,
    order: string,
    sortBy: string,
    objectId?: string,
    objectTypeId?: number,
    objectActionTypeId?: number,
    search?: string,
    startDate?: string,
    endDate?: string,
  ): Observable<QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>> => {
    // Build filter object for optional parameters
    const filter: any = {};

    if (objectId && objectId.trim().length > 0) {
      filter.objectId = objectId;
    }
    if (objectTypeId) {
      filter.objectTypeId = objectTypeId;
    }
    if (objectActionTypeId) {
      filter.objectActionTypeId = objectActionTypeId;
    }
    if (search) {
      filter.search = search;
    }
    if (startDate) {
      filter.startDate = startDate;
    }
    if (endDate) {
      filter.endDate = endDate;
    }

    // Build query params with filter as JSON string
    let params: any = {
      skip: skip.toString(),
      limit: limit.toString(),
      order,
      sortBy,
    };

    // Only add filter param if there are filters
    if (Object.keys(filter).length > 0) {
      params.filter = JSON.stringify(filter);
    }

    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path, "get-all-logs"), {
      params,
    });
  };

  streamAllAuditLogs = (
    order: string = "asc",
    sortBy: string = "id",
    objectId?: string,
    objectTypeId?: number,
    objectActionTypeId?: number,
    search?: string,
    startDate?: string,
    endDate?: string,
  ): WebSocketSubject<SocketData> => {
    const query = queryToParams(
      removeUndefined({
        order,
        sortBy,
        objectId,
        objectTypeId,
        objectActionTypeId,
        search,
        startDate,
        endDate,
      }),
    );
    const socket = webSocket({
      url: createWSUrl(this.path, `get-all-logs-ws?${query}`),
      protocol: this.jwtService.getJWTString(),
      deserializer: (msg) => JSON.parse(msg.data),
      serializer: (msg) => JSON.stringify(msg),
    });
    // Set up the WebSocket timeout to 5 minutes
    // This will automatically close the WebSocket if no messages are received within this time frame
    // This is useful for long-running connections to prevent them from hanging indefinitely
    return socket.pipe(timeout(300000)) as WebSocketSubject<SocketData>; // 5 minutes = 300,000 ms
  };

  getAllUserImpersonationLogs = (
    userImpersonationId: string,
  ): Observable<QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>> => {
    return this.httpClient.get<
      QueryResult<T & HasIdOrExternalValueOrExternalIdOrServiceName<string>>
    >(createUrl(this.path, "get-all-user-impersonation-logs", userImpersonationId));
  };
}
